import React from 'react';
import { generateCrawlablePageLinks } from '@/utils/paginationSEO';

interface PaginationSEOLinksProps {
  /**
   * Base URL without query parameters (e.g., '/articles')
   */
  baseUrl: string;

  /**
   * Total number of pages
   */
  totalPages: number;

  /**
   * Additional query params to preserve in every page link (e.g. { sort: 'newest' }).
   * The 'page' key is managed internally and must not be included here.
   */
  searchParams?: Record<string, string>;

  /**
   * Whether to render the links (default: true)
   * Set to false to disable in development
   */
  enabled?: boolean;
}

export function PaginationSEOLinks({
  baseUrl,
  totalPages,
  searchParams,
  enabled = true,
}: PaginationSEOLinksProps) {
  if (!enabled || totalPages <= 1) {
    return null;
  }

  const urlSearchParams = searchParams
    ? new URLSearchParams(searchParams as Record<string, string>)
    : undefined;

  const pageLinks = generateCrawlablePageLinks(baseUrl, totalPages, urlSearchParams);
  
  return (
    <nav
      aria-label="Pagination links"
      data-pagination-seo="true"
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      <ul>
        {pageLinks.map((url, index) => (
          <li key={index}>
            <a href={url}>Page {index + 1}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default PaginationSEOLinks;